import z from 'zod'
import { amGeometryPointSchema } from 'src/common-services/types/alphamap/geojson-types'
import { amPointSchema } from 'src/common-services/types/alphamap/generic-types'
import {
  amOwnershipEntitySearchResult,
  amOwnershipParcelCompleteSchema,
} from 'src/common-services/types/alphamap/ownership-types'
import { Bounds } from 'src/types/map'

export const SEARCH_RESULT_TYPE_ENUM = z.enum(['address', 'parcel', 'owner', 'location', 'shopping center', 'brand'])
export type SearchResultTypeEnum = z.infer<typeof SEARCH_RESULT_TYPE_ENUM>

const searchPointSchema = z.object({
  name: z.string().optional(),
  address: z.string().optional(),
  point: amPointSchema,
})

export const amSearchRequestSchema = z.object({
  points: z.array(searchPointSchema),
  bounds: z.custom<Bounds>().optional(),
  resultTypes: z.array(SEARCH_RESULT_TYPE_ENUM).optional(),
})
export type AMSearchRequest = z.infer<typeof amSearchRequestSchema>

const baseResult = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
})

// Represents google places search results
const addressResultSchema = baseResult.extend({
  type: z.literal(SEARCH_RESULT_TYPE_ENUM.Enum.address),
  meta: z.object({
    point: amGeometryPointSchema,
  }),
})
export type AddressResult = z.infer<typeof addressResultSchema>

// Represents additional AlphaMap-based results (parcels, owners, locations, shopping centers, brands)
const parcelResultSchema = baseResult.extend({
  type: z.literal(SEARCH_RESULT_TYPE_ENUM.Enum.parcel),
  meta: z.object({
    point: amOwnershipParcelCompleteSchema.shape.point,
    mCode: amOwnershipParcelCompleteSchema.shape.mCode,
    propertyType: amOwnershipParcelCompleteSchema.shape.propertyType,
    owners: amOwnershipParcelCompleteSchema.shape.owners,
    brandedLocations: amOwnershipParcelCompleteSchema.shape.brandedLocations,
    geometry: amOwnershipParcelCompleteSchema.shape.geometry,
  }),
})

const entityResultSchema = baseResult.extend({
  type: z.literal(SEARCH_RESULT_TYPE_ENUM.Enum.owner),
  meta: z.object({
    type: z.string(),
    amountOfContacts: amOwnershipEntitySearchResult.shape.amountOfContacts,
    amountOfParcels: amOwnershipEntitySearchResult.shape.amountOfParcels,
    parcelsTypesDistribution: amOwnershipEntitySearchResult.shape.parcelsTypesDistribution,
  }),
})

const locationResultSchema = baseResult.extend({
  type: z.literal(SEARCH_RESULT_TYPE_ENUM.Enum.location),
  meta: z.object({
    naicsCode: z.string().optional(),
    point: amGeometryPointSchema,
    brandId: z.string().optional(),
    shoppingCenterId: z.string().optional(),
    hasMobility: z.boolean().optional(),
  }),
})

const shoppingCenterResultSchema = baseResult.extend({
  type: z.literal(SEARCH_RESULT_TYPE_ENUM.Enum['shopping center']),
  meta: z.object({
    point: amGeometryPointSchema,
    brandIds: z.array(z.string()),
    locationIds: z.array(z.string()),
    hasMobility: z.boolean().optional(),
    naicsCode: z.string().optional(),
  }),
})

const brandResultSchema = baseResult.extend({
  type: z.literal(SEARCH_RESULT_TYPE_ENUM.Enum.brand),
  meta: z.object({
    locationsCount: z.number(),
  }),
})

const amPossibleResultsSchema = z.object({
  parcels: z.array(parcelResultSchema),
  entities: z.array(entityResultSchema),
  locations: z.array(locationResultSchema),
  shoppingCenters: z.array(shoppingCenterResultSchema),
  brands: z.array(brandResultSchema),
})
export type AMPossibleResults = z.infer<typeof amPossibleResultsSchema>

const amExtendedResultsSchema = z.object({
  nearby: amPossibleResultsSchema,
  byParcels: amPossibleResultsSchema,
  pointsMatched: z.array(z.string()),
  duration: z.string().optional(),
})
export type AMExtendedResults = z.infer<typeof amExtendedResultsSchema>

// Represents complete search results with included addresses under 'nearby'
export const amSearchResultsSchema = z.object({
  nearby: amPossibleResultsSchema.extend({
    addresses: z.array(addressResultSchema),
    other: z.array(addressResultSchema),
  }),
  byParcels: amPossibleResultsSchema,
})
export type AMSearchResults = z.infer<typeof amSearchResultsSchema>

export const amSearchResultTypeSchema = z.enum(['area', 'address', 'location', 'brand-profile', 'owner', 'parcel'])
export type AMSearchResultType = z.infer<typeof amSearchResultTypeSchema>
